import React from 'react';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { CalendarMonthSharp } from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';
import PostAddIcon from '@mui/icons-material/PostAdd';
import EditIcon from '@mui/icons-material/Edit';
import { getEditWindow, formatCutoff } from './useAbdSettings';
import { formatDate, parseDateAndTime, parseTime } from '../../../helpers/UtilityHelper';
import { formatCurrency } from '../../../common/util';
import CopyIcon from '../../../common/CopyIcon';

const CHIP_BASE = {
    height: 20,
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderRadius: '12px',
    '& .MuiChip-label': { px: '10px', py: 0 },
};

const chipSx = (palette) => ({
    ...CHIP_BASE,
    bgcolor: palette.bg,
    color: palette.color,
});

const PALETTES = {
    blue: { bg: '#e3f2fd', color: '#1565c0' },
    red: { bg: '#ffebee', color: '#c62828' },
    green: { bg: '#e8f5e9', color: '#2e7d32' },
    amber: { bg: '#fff3e0', color: '#e65100' },
    gray: { bg: '#eceff1', color: '#546e7a' },
    purple: { bg: '#f3e5f5', color: '#7b1fa2' },
    teal: { bg: '#e0f2f1', color: '#00695c' },
};

const ACTION_ICON_SX = (palette) => ({
    width: 30,
    height: 30,
    borderRadius: '6px',
    bgcolor: palette.bg,
    color: palette.color,
    '&:hover': { bgcolor: palette.bg, filter: 'brightness(0.95)' },
    '& svg': { fontSize: 16 },
});

export const buildAppointmentColumns = ({
    onSchedule,
    onEdit,
    onMarkMissed,
    onAddDocs,
    abdSettings,
    abdSettingsError = false,
    showCloseButton = true,
}) => [
    {
        field: 'merchant',
        headerName: 'Merchant',
        // Width = % of total table width. With tableLayout:fixed in the
        // ExpandableAppointmentsTable, browser distributes columns by these
        // percentages (or the explicit pixel width for very narrow cols like
        // EDD). Sums approximately 100% to keep the table inside its container
        // and avoid horizontal scroll.
        width: '11%',
        flex: 1,
        headerAlign: 'left',
        align: 'left',
        renderCell: ({ row }) => (
            <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: '#1a1a2e', wordBreak: 'break-word' }}>
                {row?.merchantDetail?.name || '-'}
            </Typography>
        ),
    },
    {
        // AWB primary (bold, top). Order ref + order date stacked below for
        // context. Merged from "AWB" + "Order Details" since both reference
        // the same shipment — saves a column without losing info.
        field: 'awbNumber',
        headerName: 'AWB / Order Details',
        width: '14%',
        flex: 1.4,
        renderCell: ({ row }) => (
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: '#1a1a2e', wordBreak: 'break-all' }}>
                        {row?.awb_number || '-'}
                    </Typography>
                    {row?.awb_number ? <CopyIcon text={row.awb_number} /> : null}
                </Box>
                {row?.reference && (
                    <Typography sx={{ fontSize: '11px', color: '#666', wordBreak: 'break-word' }}>
                        Ref: {row.reference}
                    </Typography>
                )}
                {row?.awbRegisteredDate && (
                    <Typography sx={{ fontSize: '11px', color: '#888' }}>
                        {parseDateAndTime(row.awbRegisteredDate)}
                    </Typography>
                )}
            </Box>
        ),
    },
    {
        field: 'courier',
        headerName: 'Courier',
        width: '10%',
        flex: 1,
        renderCell: ({ row }) => (
            <Box>
                <Typography sx={{ fontSize: '12.5px', color: '#333' }}>
                    {row?.courierDetail?.parent || '-'}
                </Typography>
                <Typography sx={{ fontSize: '11px', color: '#888' }}>
                    {row?.courierDetail?.prozoId}
                </Typography>
            </Box>
        ),
    },
    {
        field: 'orderStatus',
        headerName: 'Status',
        width: '11%',
        flex: 0.9,
        renderCell: ({ row }) => {
            if (!row?.orderStatus) return null;
            const isDelivered = row.orderStatus === 'DELIVERED';
            return (
                <Chip
                    size='small'
                    label={row.orderStatus}
                    sx={{ ...chipSx(isDelivered ? PALETTES.green : PALETTES.gray), maxWidth: '100%' }}
                />
            );
        },
    },
    {
        field: 'edd',
        headerName: 'EDD',
        width: '7%',
        flex: 0.7,
        renderCell: ({ row }) => (
            <Typography sx={{ fontSize: '12.5px', color: '#333' }}>
                {row?.edd ? parseDateAndTime(row.edd) : ''}
            </Typography>
        ),
    },
    {
        // Single-line full value — no ellipsis. Width tuned wide enough for
        // typical INR amounts (₹12,34,567.89 ~ 95px).
        field: 'invoiceValue',
        headerName: 'Invoice Value',
        width: '10%',
        flex: 0.8,
        renderCell: ({ row }) => (
            <Typography sx={{ fontSize: '12.5px', color: '#333', whiteSpace: 'nowrap' }}>
                {row?.invoice_value ? formatCurrency(row.invoice_value) : ''}
            </Typography>
        ),
    },
    {
        // Multi-line wrap when name is long — no ellipsis cut-off. Browser
        // breaks at word boundaries; long unbreakable tokens fall back to
        // break-word.
        // Sold-to-party name source moved from additionalData to a dedicated
        // soldToPartyDetails subdoc; fall back to legacy field for older orders.
        field: 'soldToParty',
        headerName: 'Sold To Party',
        width: '11%',
        flex: 1.1,
        renderCell: ({ row }) => {
            const partyName = row?.soldToPartyDetails?.name || row?.additionalData?.soldToPartyName;
            return (
                <Typography
                    sx={{ fontSize: '12.5px', color: '#333', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4 }}
                    title={partyName}
                >
                    {partyName || '—'}
                </Typography>
            );
        },
    },
    {
        field: 'appointmentInfo',
        headerName: 'Appointment Info',
        width: '16%',
        flex: 1.4,
        renderCell: ({ row }) => {
            const start = row?.appointmentDeliveryData?.appointment_del_start;
            const end = row?.appointmentDeliveryData?.appointment_del_end;
            const histCount = row?.appointmentDeliveryHistoryCount ?? row?.appointmentDeliveryHistory?.length ?? 0;
            const TERMINAL_FAIL_STATUSES = ['CANCELLED_ORDER', 'RTO_DELIVERED', 'LOST', 'DISPOSED_OFF', 'DAMAGED'];
            const isDelivered = row?.orderStatus === 'DELIVERED' || row?.terminalStatus?.statusVal === 'DELIVERED';
            const isTerminalFail = TERMINAL_FAIL_STATUSES.includes(row?.orderStatus) || (row?.terminalStatus && !isDelivered);
            const isTerminal = isDelivered || isTerminalFail;
            const terminalLabel = row?.terminalStatus?.statusVal || row?.orderStatus;
            const last = row?.appointmentDeliveryHistory?.slice(-1)[0];
            const hasActive = !!start && !isTerminal;

            const numSx = { fontSize: '12px', fontWeight: 700, color: '#1a1a2e' };
            const dateSx = { fontSize: '12px', color: '#333' };
            const dateStrongSx = { fontSize: '12px', color: '#1a1a2e', fontWeight: 700 };
            const lastMissedSx = { fontSize: '12px', color: '#c62828' };

            // Miss chip merged into this column (was previously its own column).
            // Renders next to the status chip when at least one miss exists.
            const missChip = histCount > 0 ? (
                <Chip
                    size='small'
                    label={`${histCount} miss${histCount > 1 ? 'es' : ''}`}
                    sx={chipSx(histCount >= 3 ? PALETTES.red : PALETTES.amber)}
                />
            ) : null;
            const chipsRow = (statusChip) => (
                <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                    {statusChip}
                    {missChip}
                </Box>
            );

            if (isDelivered) {
                const apptDate = start || last?.appointment_del_start;
                const apptEnd = end || last?.appointment_del_end;
                const apptNum = histCount + (hasActive ? 1 : 0) || 1;
                return (
                    <Box sx={{ lineHeight: 1.6 }}>
                        <Typography sx={numSx}>Appointment #{apptNum}</Typography>
                        {apptDate && (
                            <Typography sx={dateSx}>
                                <Box component='span' sx={dateStrongSx}>Date:</Box> {formatDate(apptDate, 'dd MMM, yyyy')}
                            </Typography>
                        )}
                        {apptDate && apptEnd && (
                            <Typography sx={dateSx}>
                                <Box component='span' sx={dateStrongSx}>Time:</Box> {parseTime(apptDate)} - {parseTime(apptEnd)}
                            </Typography>
                        )}
                        {chipsRow(<Chip size='small' label='DELIVERED' sx={chipSx(PALETTES.green)} />)}
                    </Box>
                );
            }

            if (isTerminal) {
                const apptDate = start || last?.appointment_del_start;
                const apptEnd = end || last?.appointment_del_end;
                const apptNum = histCount + (start ? 1 : 0) || histCount || 0;
                return (
                    <Box sx={{ lineHeight: 1.6 }}>
                        {apptNum > 0 && <Typography sx={numSx}>Appointment #{apptNum}</Typography>}
                        {apptDate && (
                            <Typography sx={dateSx}>
                                <Box component='span' sx={dateStrongSx}>Date:</Box> {formatDate(apptDate, 'dd MMM, yyyy')}
                            </Typography>
                        )}
                        {apptDate && apptEnd && (
                            <Typography sx={dateSx}>
                                <Box component='span' sx={dateStrongSx}>Time:</Box> {parseTime(apptDate)} - {parseTime(apptEnd)}
                            </Typography>
                        )}
                        {chipsRow(
                            <Chip size='small' label={String(terminalLabel || 'CLOSED').replace(/_/g, ' ')} sx={chipSx(PALETTES.gray)} />,
                        )}
                    </Box>
                );
            }

            if (hasActive) {
                const apptNum = histCount + 1;
                const edits = row?.appointmentDeliveryData?.appointment_del_edits || [];
                const hasEdits = edits.length > 0;
                return (
                    <Box sx={{ lineHeight: 1.6 }}>
                        <Typography sx={numSx}>Appointment #{apptNum}</Typography>
                        <Typography sx={dateSx}>
                            <Box component='span' sx={dateStrongSx}>Date:</Box> {formatDate(start, 'dd MMM, yyyy')}
                        </Typography>
                        {end && (
                            <Typography sx={dateSx}>
                                <Box component='span' sx={dateStrongSx}>Time:</Box> {parseTime(start)} - {parseTime(end)}
                            </Typography>
                        )}
                        {chipsRow(
                            <>
                                <Chip size='small' label='ACTIVE' sx={chipSx(PALETTES.blue)} />
                                {hasEdits && (
                                    <Tooltip title={`Edited ${edits.length} time${edits.length > 1 ? 's' : ''}. Expand the row for the edit log.`}>
                                        <Chip size='small' label='EDITED' sx={chipSx(PALETTES.purple)} tabIndex={0} />
                                    </Tooltip>
                                )}
                            </>
                        )}
                    </Box>
                );
            }

            const apptNum = histCount + 1;
            return (
                <Box sx={{ lineHeight: 1.6 }}>
                    <Typography sx={numSx}>Appointment #{apptNum}</Typography>
                    {last?.appointment_del_start && (
                        <Typography sx={lastMissedSx}>
                            <Box component='span' sx={{ ...lastMissedSx, fontWeight: 700 }}>Last missed:</Box> {formatDate(last.appointment_del_start, 'dd MMM, yyyy')}
                        </Typography>
                    )}
                    {chipsRow(<Chip size='small' label='NEEDS SCHEDULING' sx={chipSx(PALETTES.red)} />)}
                </Box>
            );
        },
    },
    {
        field: 'action',
        headerName: 'Actions',
        width: '10%',
        flex: 1,
        renderCell: ({ row }) => {
            const TERMINAL_FAIL_STATUSES = ['CANCELLED_ORDER', 'RTO_DELIVERED', 'LOST', 'DISPOSED_OFF', 'DAMAGED'];
            const hasActive = !!row?.appointmentDeliveryData?.appointment_del_start;
            const isDelivered = row?.orderStatus === 'DELIVERED' || row?.terminalStatus?.statusVal === 'DELIVERED';
            const isTerminalFail = TERMINAL_FAIL_STATUSES.includes(row?.orderStatus) || (row?.terminalStatus && !isDelivered);
            const isTerminal = isDelivered || isTerminalFail;
            return (
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Tooltip title='Order Details'>
                        <IconButton
                            component={Link}
                            to={`/order-detail/${row.awb_number}`}
                            sx={ACTION_ICON_SX({ bg: '#e3f2fd', color: '#1565c0' })}
                        >
                            <VisibilityIcon />
                        </IconButton>
                    </Tooltip>
                    {!isTerminal && !hasActive && (
                        <Tooltip title='Schedule Appointment'>
                            <IconButton
                                onClick={() => onSchedule?.(row)}
                                sx={ACTION_ICON_SX({ bg: '#e8f5e9', color: '#2e7d32' })}
                            >
                                <CalendarMonthSharp />
                            </IconButton>
                        </Tooltip>
                    )}
                    {!isTerminal && hasActive && onEdit && (() => {
                        const { configured, editable, until } = getEditWindow(row, abdSettings);
                        const title = abdSettingsError
                            ? "Couldn't load the edit cut-off. Refresh the page."
                            : !configured
                            ? 'Loading the edit cut-off…'
                            : editable
                                ? `Edit Appointment (editable until ${formatCutoff(until)})`
                                : `Edit window closed at ${formatCutoff(until)}. Use Mark as Missed.`;
                        return (
                            <Tooltip title={title}>
                                <span>
                                    <IconButton
                                        onClick={() => onEdit?.(row)}
                                        disabled={!editable}
                                        sx={ACTION_ICON_SX(editable ? PALETTES.teal : PALETTES.gray)}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        );
                    })()}
                    {!isTerminal && showCloseButton && hasActive && (
                        <Tooltip title='Mark as Missed'>
                            <IconButton
                                onClick={() => onMarkMissed?.(row)}
                                sx={ACTION_ICON_SX({ bg: '#fff3e0', color: '#e65100' })}
                            >
                                <CancelIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    {!isTerminal && hasActive && (
                        <Tooltip title='Add Documents to Appointment'>
                            <IconButton
                                onClick={() => onAddDocs?.(row)}
                                sx={ACTION_ICON_SX({ bg: '#ede7f6', color: '#5e35b1' })}
                            >
                                <PostAddIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            );
        },
    },
];
